import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from './button';
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from './sheet';
import { ScrollArea } from './scroll-area';
import { Dialog, DialogContent } from './dialog';
import { 
  Menu, X, Home, ClipboardCheck, UserPlus, FileEdit, ShieldCheck,
  Calculator, FileSpreadsheet, Presentation, GitBranch, Award, CheckCircle2,
  FileText, Banknote, Bell, Plus, Settings, MessageSquare, LogOut, UserCheck,
  ArrowRightLeft, Sparkles, Mail, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import ShareModal from './share-modal';

const NavMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/login');
  };

  const handleShare = () => {
    setMenuOpen(false);
    setShowShareModal(true);
  };

  const handlePlaceholder = (featureName) => {
    setMenuOpen(false);
    setSelectedFeature(featureName);
    setShowContactModal(true);
  };

  const handleNavigate = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const menuSections = [
    {
      id: 'all-work',
      label: 'ALL WORK',
      color: 'text-orange-600',
      barColor: 'bg-orange-500',
      items: [
        { id: 'home', icon: Home, title: 'Home', action: () => handleNavigate('/tradie'), active: true },
      ],
    },
    {
      id: 'win-work',
      label: 'WIN WORK',
      color: 'text-emerald-600',
      barColor: 'bg-emerald-500',
      items: [
        { id: 'leads-referral', icon: UserPlus, title: 'Lead / Referral', action: () => handleNavigate('/tradie/leads-referral'), active: true },
        { id: 'generate-scope', icon: FileEdit, title: 'Generate Scope', action: () => handleNavigate('/tradie/capture-job-notes'), active: true },
        { id: 'costing', icon: Calculator, title: 'Costing', action: () => handlePlaceholder('Costing'), active: false },
        { id: 'quote', icon: FileSpreadsheet, title: 'Quote', action: () => handlePlaceholder('Quote'), active: false },
        { id: 'proposal', icon: Presentation, title: 'Proposal', action: () => handlePlaceholder('Proposal'), active: false },
        { id: 'create-win', icon: Plus, title: 'Create Your Own', action: () => handlePlaceholder('Custom WIN WORK Module'), active: false, isCreate: true },
      ],
    },
    {
      id: 'deliver-work',
      label: 'DELIVER WORK',
      color: 'text-blue-600',
      barColor: 'bg-blue-500',
      items: [
        { id: 'log-update', icon: ClipboardCheck, title: 'Site Update', action: () => handleNavigate('/tradie/log-update'), active: true },
        { id: 'generate-swms', icon: ShieldCheck, title: 'SWMS', action: () => handleNavigate('/tradie/generate-swms'), active: true },
        { id: 'record-signoff', icon: UserCheck, title: 'Record Sign-Off', action: () => handlePlaceholder('Record Sign-Off'), active: false },
        { id: 'variation', icon: GitBranch, title: 'Variation', action: () => handlePlaceholder('Variation'), active: false },
        { id: 'certificate', icon: Award, title: 'Certificate of Compliance', action: () => handlePlaceholder('Certificate of Compliance'), active: false },
        { id: 'job-completion', icon: CheckCircle2, title: 'Job Completion', action: () => handlePlaceholder('Job Completion'), active: false },
        { id: 'create-deliver', icon: Plus, title: 'Create Your Own', action: () => handlePlaceholder('Custom DELIVER WORK Module'), active: false, isCreate: true },
      ],
    },
    {
      id: 'get-paid',
      label: 'GET PAID',
      color: 'text-orange-600',
      barColor: 'bg-orange-500',
      items: [
        { id: 'create-claim', icon: FileText, title: 'Create Claim / Invoice', action: () => handlePlaceholder('Create Claim / Invoice'), active: false },
        { id: 'log-payment', icon: Banknote, title: 'Log Payment', action: () => handlePlaceholder('Log Payment'), active: false },
        { id: 'send-reminder', icon: Bell, title: 'Send Reminder', action: () => handlePlaceholder('Send Reminder'), active: false },
        { id: 'create-paid', icon: Plus, title: 'Create Your Own', action: () => handlePlaceholder('Custom GET PAID Module'), active: false, isCreate: true },
      ],
    },
    {
      id: 'tap-to-tap',
      label: 'TAP-TO-TAP',
      color: 'text-violet-600',
      barColor: 'bg-violet-500',
      items: [
        { id: 'tap-to-tap', icon: ArrowRightLeft, title: 'Tap-to-Tap', action: () => handlePlaceholder('Tap-to-Tap'), active: false },
      ],
    },
    {
      id: 'utility',
      label: 'Utility',
      items: [
        { id: 'share', icon: Share2, title: 'Share 2Tap', action: handleShare, active: true },
        { id: 'settings', icon: Settings, title: 'Settings', action: () => handlePlaceholder('Settings'), active: false },
        { id: 'contact', icon: MessageSquare, title: 'Contact 2Tap Team', action: () => { 
          setMenuOpen(false); 
          const subject = encodeURIComponent('2Tap Support Request');
          const body = encodeURIComponent(`Hi 2Tap Team,

I'd like to get in touch about:

[Please describe what you need help with]

Thanks`);
          window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`; 
        }, active: true },
        { id: 'logout', icon: LogOut, title: 'Log Out', action: handleLogout, active: true, isLogout: true },
      ],
    },
  ];

  return (
    <>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            data-testid="nav-menu-btn"
            className="text-slate-300 hover:text-white hover:bg-slate-700 h-9 w-9"
          >
            <Menu className="w-5 h-5" strokeWidth={2} />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[280px] p-0 bg-slate-50">
          <SheetHeader className="bg-[#1F2A37] text-white p-4 text-left relative">
            <div className="h-1 bg-orange-500 absolute top-0 left-0 right-0"></div>
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute right-3 top-3 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
              data-testid="menu-close-btn"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
            <div className="pr-8">
              <h2 className="text-lg font-bold">2Tap</h2>
              <p className="text-orange-300 text-xs font-medium">ALL WORK</p>
            </div>
          </SheetHeader>
          
          <ScrollArea className="h-[calc(100vh-80px)]">
            <div className="py-1">
              {menuSections.map((section, sectionIndex) => (
                <div key={section.id} className={sectionIndex > 0 ? 'mt-1' : ''}>
                  <div className="flex items-center gap-2 px-4 py-1.5">
                    {section.barColor && <div className={`w-1 h-3 ${section.barColor} rounded-full`}></div>}
                    <span className={`text-[10px] font-bold tracking-wider ${section.color || 'text-slate-500'}`}>
                      {section.label}
                    </span>
                  </div>
                  
                  <div className="space-y-0">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        data-testid={`menu-${item.id}`}
                        onClick={item.action}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2 text-left
                          transition-colors hover:bg-slate-100 active:bg-slate-200
                          ${item.isLogout ? 'text-red-600 hover:bg-red-50' : ''}
                          ${item.isCreate ? 'text-slate-500' : ''}
                        `}
                      >
                        <item.icon className={`w-4 h-4 ${item.isLogout ? 'text-red-500' : item.isCreate ? 'text-slate-400' : 'text-slate-600'}`} strokeWidth={2} />
                        <span className={`text-sm font-medium ${item.isLogout ? 'text-red-600' : item.isCreate ? 'text-slate-500' : 'text-slate-700'}`}>
                          {item.title}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  {sectionIndex < menuSections.length - 1 && (
                    <div className="border-b border-slate-200 mt-1"></div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="px-4 py-3 border-t border-slate-200 mt-1">
              <p className="text-xs text-slate-500">
                Logged in as <span className="font-semibold text-slate-600">{user?.name}</span>
              </p>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Feature Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-sm mx-4 rounded-2xl border-0 p-0 overflow-hidden">
          {/* Compact header with gradient */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 py-5">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {selectedFeature}
              </h2>
            </div>
          </div>
          
          {/* Body */}
          <div className="px-6 py-5">
            <p className="text-slate-600 text-center text-sm mb-5">
              This feature can be added to your 2Tap setup. Get in touch with the team to discuss your requirements.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                data-testid="contact-team-btn"
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl"
                onClick={() => {
                  setShowContactModal(false);
                  const subject = encodeURIComponent(`2Tap Feature Request: ${selectedFeature}`);
                  const body = encodeURIComponent(`Hi 2Tap Team,

I'm interested in the **${selectedFeature}** feature in 2Tap.

Here are my details:
• Name: ${user?.name || 'N/A'}
• Email: ${user?.email || 'N/A'}

Appreciate it — keen to hear what's possible.

Thanks 2Tap!`);
                  window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`;
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact 2Tap Team
              </Button>
              <Button
                data-testid="close-modal-btn"
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                onClick={() => setShowContactModal(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Share Modal */}
      <ShareModal open={showShareModal} onClose={() => setShowShareModal(false)} />
    </>
  );
};

export default NavMenu;
